import React, { useState, useEffect, useRef } from "react";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { fetchPackages, deletePackage, togglePackageStatus, getPackageSubscribers, migratePackageSubscribers, transformPackagesData } from "action/packageAction";
import CreatePackageModal from "components/Modals/CreatePackageModal";
import EditPackageModal from "components/Modals/EditPackageModal";
import ViewPackageModal from "components/Modals/ViewPackageModal";

/* ── Icons ── */
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const MoreVIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
  </svg>
);
const BoxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const DatabaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);
const PowerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M18.36 6.64a9 9 0 11-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

/* ── Row dropdown ── */
function PkgRowMenu({ pkg, onView, onEdit, onToggle, onDelete }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const close = () => setOpen(false);
  const isActive = pkg.status === "Active";
  return (
    <div className="lms-dropdown-wrapper" ref={ref}>
      <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" onClick={() => setOpen((o) => !o)}>
        <MoreVIcon />
      </button>
      {open && (
        <div className="lms-dropdown" style={{ right: 0, minWidth: 180 }}>
          <button className="lms-dropdown-item" onClick={() => { close(); onView(pkg); }}><EyeIcon /> {t("Lihat Detail")}</button>
          <button className="lms-dropdown-item" onClick={() => { close(); onEdit(pkg); }}><EditIcon /> {t("Edit Package")}</button>
          <button
            className="lms-dropdown-item"
            style={{ color: isActive ? "var(--warning-600,#d97706)" : "var(--success-600)" }}
            onClick={() => { close(); onToggle(pkg); }}
          >
            <PowerIcon /> {isActive ? t("Non-aktifkan") : t("Aktifkan")}
          </button>
          <div className="lms-dropdown-divider" />
          <button
            className="lms-dropdown-item danger"
            disabled={isActive}
            title={isActive ? t("Non-aktifkan dulu sebelum menghapus") : ""}
            onClick={() => { if (!isActive) { close(); onDelete(pkg); } }}
          >
            <TrashIcon /> {t("Hapus Package")}
            {isActive && <span style={{ fontSize: 10, color: "var(--text-muted)", marginLeft: "auto" }}>{t("Inactive dulu")}</span>}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Move tenants dialog ── */
function MoveTenantsDialog({ open, fromPkg, packages, loading, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const [toPkgId, setToPkgId] = useState("");
  useEffect(() => { if (open) setToPkgId(""); }, [open]);
  const options = packages.filter((p) => p.id !== fromPkg?.id);
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", zIndex: 95, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18)", width: "100%", maxWidth: 460, overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div className="t-h2">{t("Pindahkan Tenant")}</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
            {t("Package")} <strong>{fromPkg?.name}</strong> {t("masih digunakan oleh tenant. Pilih package pengganti sebelum menghapus.")}
          </div>
        </div>
        <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="lms-form-group" style={{ marginBottom: 0 }}>
            <label className="lms-label">{t("Pindahkan ke Package")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
            <select className="lms-input" value={toPkgId} onChange={(e) => setToPkgId(e.target.value)}>
              <option value="">-- Pilih package tujuan --</option>
              {options.map((p) => (
                <option key={p.id} value={p.id}>{p.name} — Rp {p.price.toLocaleString("id-ID")}</option>
              ))}
            </select>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)", background: "var(--warning-50,#fffbeb)", border: "1px solid var(--warning-200,#fde68a)", borderRadius: 8, padding: "8px 12px" }}>
            {t("Semua tenant yang menggunakan paket ini akan otomatis dipindahkan ke package tujuan.")}
          </div>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={onCancel} disabled={loading}>{t("Batal")}</button>
          <button className="lms-btn lms-btn-primary" onClick={() => toPkgId && onConfirm(toPkgId)} disabled={loading || !toPkgId}>
            {loading ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <ArrowRightIcon />}
            {loading ? t("Memindahkan...") : t("Pindahkan & Hapus")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Confirm dialog ── */
function ConfirmDialog({ open, title, message, loading, onConfirm, onCancel }) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18)", width: "100%", maxWidth: 440, overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div className="t-h2">{title}</div>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <div className="t-body">{message}</div>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={onCancel} disabled={loading}>{t("Batal")}</button>
          <button className="lms-btn lms-btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <TrashIcon />}
            {t("Hapus")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Package card ── */
function PackageCard({ pkg, onEdit, onView, onToggle, onDelete, index }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const benefits = Array.isArray(pkg.benefits) ? pkg.benefits : [];
  const visible = showAll ? benefits : benefits.slice(0, 5);

  const ACCENT_COLORS = [
    "linear-gradient(90deg,#4F46E5,#818CF8)",
    "linear-gradient(90deg,#0EA5E9,#38BDF8)",
    "linear-gradient(90deg,#10B981,#34D399)",
    "linear-gradient(90deg,#F59E0B,#FCD34D)",
    "linear-gradient(90deg,#8B5CF6,#A78BFA)",
    "linear-gradient(90deg,#EC4899,#F472B6)",
  ];
  const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];

  return (
    <div className="lms-pkg-card" style={{ "--pkg-accent": accent }}>
      {/* Colored top bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent, borderRadius: "var(--r-xl) var(--r-xl) 0 0" }} />

      {/* Head: name + badge + menu */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: "var(--text-strong)", lineHeight: 1.3 }}>{pkg.name}</div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>#{pkg.id}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span className={`lms-badge lms-badge-${pkg.status === "Active" ? "success" : "neutral"}`}>
            {pkg.status === "Active" ? t("Active") : t("Inactive")}
          </span>
          <PkgRowMenu
            pkg={pkg}
            onView={onView}
            onEdit={onEdit}
            onToggle={onToggle}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Price */}
      <div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "var(--brand-600)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          Rp {pkg.price.toLocaleString("id-ID")}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{t("per bulan")}</div>
      </div>

      {/* Storage */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-muted)", background: "var(--slate-100)", borderRadius: 8, padding: "5px 10px", alignSelf: "flex-start" }}>
        <DatabaseIcon />
        <span style={{ fontWeight: 600, color: "var(--text-strong)" }}>{pkg.storage_gb || 0} GB</span>
        <span>Storage</span>
      </div>

      {/* Benefits */}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          {benefits.length} {t("Benefit")}{benefits.length !== 1 ? "s" : ""}
        </div>
        {benefits.length > 0 ? (
          <>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 7 }}>
              {visible.map((b, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 13, color: "var(--text-base)" }}>
                  <span style={{ width: 17, height: 17, borderRadius: "50%", background: "var(--success-100)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--success-600)", flexShrink: 0, marginTop: 1 }}>
                    <CheckIcon />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            {benefits.length > 5 && (
              <button
                className="lms-btn lms-btn-ghost lms-btn-sm"
                style={{ marginTop: 8, fontSize: 12, color: "var(--brand-600)" }}
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? t("Lihat lebih sedikit ↑") : `+${benefits.length - 5} benefit lainnya ↓`}
              </button>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>{t("Belum ada benefit")}</div>
        )}
      </div>
    </div>
  );
}

function MasterPackage() {
  const { t } = useTranslation();
  React.useEffect(() => { document.title = "Master Package | LMS Tenant"; }, []);

  const toast = useToast();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [filterName, setFilterName] = useState("");
  const [meta, setMeta] = useState(null);
  // delete flow
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveSaving, setMoveSaving] = useState(false);
  // modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const result = await fetchPackages({ search: filterName, per_page: 50 });
      if (result.success) {
        setPackages(transformPackagesData(result.data));
        setMeta(result.meta);
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to load packages"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("An error occurred while loading packages"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPackages(); }, [filterName]);

  const handleToggleStatus = async (pkg) => {
    try {
      const result = await togglePackageStatus(pkg.id);
      if (result.success) {
        const label = result.data?.status || (pkg.status === "Active" ? "Inactive" : "Active");
        toast({ title: t("Status Diperbarui"), description: `${pkg.name} sekarang ${label}.`, status: "success", duration: 3000, isClosable: true });
        await loadPackages();
      } else {
        toast({ title: t("Error"), description: result.message || t("Gagal mengubah status"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Terjadi kesalahan"), status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleDeleteClick = async (pkg) => {
    if (pkg.status === "Active") {
      toast({ title: t("Tidak Bisa Dihapus"), description: t("Non-aktifkan package terlebih dahulu sebelum menghapus."), status: "warning", duration: 4000, isClosable: true });
      return;
    }
    setSelectedPackage(pkg);
    // check subscribers
    try {
      const res = await getPackageSubscribers(pkg.id);
      if (res.success && res.count > 0) {
        setMoveOpen(true);
      } else {
        setDeleteOpen(true);
      }
    } catch {
      setDeleteOpen(true);
    }
  };

  const handleMoveAndDelete = async (toPkgId) => {
    if (!selectedPackage) return;
    setMoveSaving(true);
    try {
      const migrateResult = await migratePackageSubscribers(selectedPackage.id, toPkgId);
      if (!migrateResult.success) {
        toast({ title: t("Error"), description: migrateResult.message || t("Gagal memindahkan tenant"), status: "error", duration: 3000, isClosable: true });
        return;
      }
      const deleteResult = await deletePackage(selectedPackage.id);
      if (deleteResult.success) {
        toast({ title: t("Package Dihapus"), description: `${migrateResult.count || 0} tenant dipindahkan dan package dihapus.`, status: "success", duration: 4000, isClosable: true });
        setMoveOpen(false);
        await loadPackages();
      } else {
        toast({ title: t("Error"), description: deleteResult.message || t("Gagal menghapus package"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Terjadi kesalahan"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setMoveSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedPackage) return;
    setDeleteSaving(true);
    try {
      const result = await deletePackage(selectedPackage.id);
      if (result.success) {
        toast({ title: t("Package Dihapus"), description: result.message || t("Package berhasil dihapus."), status: "success", duration: 3000, isClosable: true });
        await loadPackages();
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to delete package"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("An error occurred while deleting package"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setDeleteSaving(false);
      setDeleteOpen(false);
    }
  };

  const activeCount = packages.filter((p) => p.status === "Active").length;

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>Apps</span><ChevronRightIcon /><span>Admin</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Master Package")}</span>
          </div>
          <div className="t-display">{t("Master Package")}</div>
          <div className="t-small">{t("Kelola paket layanan yang tersedia untuk tenant.")}</div>
        </div>
        <button className="lms-btn lms-btn-primary" onClick={() => setCreateOpen(true)}>
          <PlusIcon /> {t("Tambah Package")}
        </button>
      </div>

      {/* Stats strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
        {[
          {
            label: t("Total Package"),
            value: packages.length,
            color: "var(--brand-600)",
            bg: "linear-gradient(135deg,#EEF2FF,#E0E7FF)",
            iconBg: "linear-gradient(135deg,#4F46E5,#818CF8)",
            icon: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            ),
          },
          {
            label: t("Active"),
            value: activeCount,
            color: "var(--success-600)",
            bg: "linear-gradient(135deg,#ECFDF5,#D1FAE5)",
            iconBg: "linear-gradient(135deg,#059669,#34D399)",
            icon: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            ),
          },
          {
            label: t("Inactive"),
            value: packages.length - activeCount,
            color: "#B45309",
            bg: "linear-gradient(135deg,#FFFBEB,#FEF3C7)",
            iconBg: "linear-gradient(135deg,#D97706,#FBBF24)",
            icon: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
            ),
          },
        ].map(({ label, value, color, bg, iconBg, icon }) => (
          <div key={label} className="lms-card" style={{ padding: "18px 20px", marginBottom: 0, background: bg, border: "none" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: "-0.02em", lineHeight: 1 }}>{loading ? "—" : value}</div>
              </div>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                {icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="lms-card">
        {/* Filter bar */}
        <div className="lms-card-body" style={{ paddingBottom: 16 }}>
          <div className="lms-filter-bar">
            <div className="lms-input-wrap" style={{ flex: 1, maxWidth: 360 }}>
              <SearchIcon />
              <input
                className="lms-input"
                placeholder={t("Cari nama package…")}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
            </div>
            {filterName && (
              <button className="lms-btn lms-btn-ghost lms-btn-sm" onClick={() => setFilterName("")}>
                <XIcon /> {t("Clear")}
              </button>
            )}
            {!loading && (
              <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-muted)" }}>
                <strong style={{ color: "var(--text-strong)" }}>{meta?.total || packages.length}</strong> {t("package")}
              </div>
            )}
          </div>
        </div>

        <div className="lms-card-body" style={{ paddingTop: 0 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 64 }}>
              <div className="lms-spinner" style={{ width: 32, height: 32 }} />
            </div>
          ) : packages.length === 0 ? (
            <div className="lms-empty">
              <div className="lms-empty-icon"><BoxIcon /></div>
              <div className="t-h3">{t("Tidak ada package ditemukan")}</div>
              <div className="t-small">{t("Coba ubah filter atau buat package baru.")}</div>
              <button className="lms-btn lms-btn-primary" style={{ marginTop: 16 }} onClick={() => setCreateOpen(true)}>
                <PlusIcon /> {t("Tambah Package")}
              </button>
            </div>
          ) : (
            <div className="lms-pkg-grid">
              {packages.map((pkg, i) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  index={i}
                  onEdit={(p) => { setSelectedPackage(p); setEditOpen(true); }}
                  onView={(p) => { setSelectedPackage(p); setViewOpen(true); }}
                  onToggle={handleToggleStatus}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>

        {meta && !loading && meta.last_page > 1 && (
          <div className="lms-pagination">
            <span className="lms-pagination-info">
              Showing <strong>{meta.from || 0}</strong>–<strong>{meta.to || 0}</strong> of <strong>{meta.total}</strong>
              {` (Halaman ${meta.current_page} dari ${meta.last_page})`}
            </span>
          </div>
        )}
      </div>

      <CreatePackageModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onSuccess={loadPackages} />
      <EditPackageModal isOpen={editOpen} onClose={() => setEditOpen(false)} package={selectedPackage} onSuccess={loadPackages} />
      <ViewPackageModal isOpen={viewOpen} onClose={() => setViewOpen(false)} package={selectedPackage} />
      <ConfirmDialog
        open={deleteOpen}
        title={t("Hapus Package")}
        message={<>Apakah Anda yakin ingin menghapus package <strong>"{selectedPackage?.name}"</strong>? {t("Tindakan ini tidak dapat dibatalkan.")}</>}
        loading={deleteSaving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
      <MoveTenantsDialog
        open={moveOpen}
        fromPkg={selectedPackage}
        packages={packages}
        loading={moveSaving}
        onConfirm={handleMoveAndDelete}
        onCancel={() => { setMoveOpen(false); setSelectedPackage(null); }}
      />
    </div>
  );
}

export default MasterPackage;
